import type { TFunction } from 'i18next';
import { formatUnit } from '../../utils/units';
import { formatDateEuropean } from '../../utils/dateUtils';
import { formatLocalizedNumber, formatLocalizedQuantity } from '../../utils/numberFormatting';
import { CARGO_QUANTITY_TOLERANCE } from '../../utils/transportCargoAllocation';

/**
 * operationalExceptionText.ts — single home for the human copy of every
 * operational-exception / consistency warning (owner decision 2026-09-21).
 *
 * Rules that must not be broken here:
 *  - a warning code is NEVER shown as the sentence an operator reads. An
 *    unknown code falls back to the backend's Polish `message`, and only then
 *    to a neutral Polish sentence; the raw code may appear solely as the small
 *    grey support caption (`supportCode`).
 *  - one warning = one short Polish paragraph, "what happens and why", written
 *    for a warehouse operator: no English, no enums, no SKUs.
 *  - consistency warnings (decision 2026-09-18) carry quantities, purchase
 *    prices and document numbers only — never customer sale prices or margin.
 *
 * Both readers of this copy use this module: the confirmation dialog
 * (`operationalExceptionFlow.tsx`) and the Rozbieżności registry
 * (`pages/OperationalExceptions.tsx`), so the register shows the very sentence
 * the operator confirmed.
 */

export interface OperationalWarning {
  code: string;
  /** Polish sentence produced by the backend; the fallback for unknown codes. */
  message?: string;
  details?: Record<string, any>;
}

export interface OperationalWarningCopy {
  /** The Polish sentence an operator reads. Never a bare code. */
  text: string;
  /** Set only when the sentence could not be derived from the code. */
  supportCode: string | null;
}

const WARNING_KEY = 'common:operationalException.warnings';

/**
 * Every warning code the backend can confirm, in the order the Rozbieżności
 * filter lists them. It lives beside the copy so a new code cannot become
 * filterable without a Polish sentence; the WZ aliases below share one
 * sentence and are not separate filter entries.
 */
export const OPERATIONAL_EXCEPTION_CODES = [
  'WZ_QUANTITY_DIFFERS_FROM_ORDER',
  'PURCHASE_PRICE_DEVIATES',
  'WZ_VARIANT_DIFFERS_FROM_ORDER',
  'DELIVERED_WITHOUT_RELEASED_WZ',
  'PZ_QUANTITY_RECEIVED_EXCEEDS_ORDERED',
  'PZ_INVOICE_DATE_AFTER_RECEIPT',
  'PZ_INVOICE_NET_AMOUNT_MISMATCH',
  'CARGO_OVER_ALLOCATION',
  'TRANSPORT_DELIVERY_EXCEEDS_PLANNED',
  'TRANSPORT_ACTUAL_DELIVERY_BEFORE_LOADING',
  'WZ_MISSING_PURCHASE_INVOICE',
  // WP1 "niczego nie blokujemy" (decision 2026-09-24): former locks.
  'WZ_CORRECTED_AFTER_INVOICE',
  'SUPPLIER_ORDER_PZ_QUANTITY_REDUCED',
  // WP1 part 2: a ZK / ZD changed after its approved invoice.
  'ORDER_CHANGED_AFTER_INVOICE',
  'SUPPLIER_ORDER_CHANGED_AFTER_INVOICE',
  // WP2: automatic entry, stock issued beyond the invoiced quantity.
  'ORDER_ISSUED_OVER_INVOICE',
] as const;

const quantity = (value: unknown) => formatLocalizedQuantity(Number(value) || 0, 'pl');

/** Money always reads with two Polish decimals: 33,10 — never 33.1. */
const money = (value: unknown) => formatLocalizedNumber(Number(value) || 0, 2, 'pl');

/**
 * Percent without a decimal that carries no information: 55%, and 7,5% only
 * where a tenth of a percent still changes the reading (a small threshold).
 */
const percent = (value: unknown) => {
  const numeric = Number(value) || 0;
  const rounded = Math.round(numeric);
  const decimalMatters = Math.abs(numeric) < 10 && Math.abs(numeric - rounded) >= 0.05;
  return decimalMatters
    ? formatLocalizedNumber(numeric, 1, 'pl')
    : formatLocalizedNumber(rounded, 0, 'pl');
};

const unit = (value: unknown) => formatUnit(typeof value === 'string' ? value : null, 'pl');

/**
 * Polish unit labels end with their own period (`szt.`), so a sentence that
 * closes on a unit would read "226 szt..". The sentence keeps its full stop in
 * the dictionary; the duplicate is removed here, once, for every code.
 */
const finishSentence = (text: string) => text.replace(/\.\.(?=$|\s)/g, '.');

/**
 * A backend `message` is only usable as operator copy when it is a sentence.
 * An ALL_CAPS_CODE echoed as the message would reintroduce exactly the bug
 * this module exists to prevent.
 */
function isReadableSentence(message: unknown): message is string {
  const text = typeof message === 'string' ? message.trim() : '';
  if (text.length < 8 || !text.includes(' ')) return false;
  return !/^[A-Z0-9_ ]+$/.test(text);
}

/**
 * "what changes" list for the after-invoice warnings: field labels and line
 * names only (the backend never sends the new values, so no sale price leaks).
 */
function afterInvoiceChanges(details: Record<string, any>, t: TFunction): string {
  const changes = Array.isArray(details.changes) ? details.changes : [];
  const fieldList = (fields: unknown) => (Array.isArray(fields) ? fields : [])
    .map((field) => String(t(`${WARNING_KEY}.afterInvoiceFields.${field}`, { defaultValue: String(t(`${WARNING_KEY}.afterInvoiceFields.other`)) })))
    .filter((label, index, labels) => labels.indexOf(label) === index)
    .join(', ');
  const parts = changes.map((change: Record<string, any>) => {
    if (change.scope === 'HEADER') {
      return String(t(`${WARNING_KEY}.afterInvoiceHeader`, { fields: fieldList(change.fields) }));
    }
    const productName = change.name || String(t(`${WARNING_KEY}.afterInvoiceUnnamedLine`));
    if (change.change === 'ADDED') return String(t(`${WARNING_KEY}.afterInvoiceLineAdded`, { productName }));
    if (change.change === 'REMOVED') return String(t(`${WARNING_KEY}.afterInvoiceLineRemoved`, { productName }));
    return String(t(`${WARNING_KEY}.afterInvoiceLineUpdated`, { productName, fields: fieldList(change.fields) }));
  });
  return parts.join('; ');
}

function afterInvoiceNumbers(details: Record<string, any>, t: TFunction): string {
  const invoiceNumbers = Array.isArray(details.invoiceNumbers) ? details.invoiceNumbers.filter(Boolean) : [];
  return invoiceNumbers.length > 0
    ? String(t(`${WARNING_KEY}.afterInvoiceNumbers`, { invoiceNumbers: invoiceNumbers.join(', ') }))
    : '';
}

function sentenceForCode(warning: OperationalWarning, t: TFunction): string | null {
  const details = warning.details || {};
  switch (warning.code) {
    case 'WZ_QUANTITY_DIFFERS_FROM_ORDER': {
      const alreadyReleased = Number(details.alreadyReleasedQuantity) || 0;
      return String(t(`${WARNING_KEY}.wzQuantityDiffersFromOrder`, {
        productName: details.productName,
        unit: unit(details.unit),
        issuedQuantity: quantity(details.issuedQuantity),
        orderQuantity: quantity(details.orderQuantity),
        breakdown: alreadyReleased > 0
          ? String(t(`${WARNING_KEY}.wzQuantityDiffersFromOrderBreakdown`, {
              unit: unit(details.unit),
              wzQuantity: quantity(details.wzQuantity),
              alreadyReleasedQuantity: quantity(alreadyReleased),
            }))
          : '',
      }));
    }
    case 'WZ_VARIANT_DIFFERS_FROM_ORDER':
      return String(t(`${WARNING_KEY}.wzVariantDiffersFromOrder`, {
        productName: details.productName,
        wzProductName: details.wzProductName || details.productName,
      }));
    case 'PURCHASE_PRICE_DEVIATES': {
      const unitCost = Number(details.unitCost) || 0;
      const medianUnitCost = Number(details.medianUnitCost) || 0;
      return String(t(`${WARNING_KEY}.purchasePriceDeviates`, {
        productName: details.productName,
        currency: details.currency,
        pricingUnit: unit(details.pricingUnit),
        unitCost: money(unitCost),
        medianUnitCost: money(medianUnitCost),
        deviationPercent: percent(details.deviationPercent),
        comparison: String(t(unitCost >= medianUnitCost
          ? `${WARNING_KEY}.purchasePriceHigher`
          : `${WARNING_KEY}.purchasePriceLower`)),
      }));
    }
    case 'DELIVERED_WITHOUT_RELEASED_WZ': {
      const wzNumbers = Array.isArray(details.wzNumbers) ? details.wzNumbers : [];
      return String(t(`${WARNING_KEY}.deliveredWithoutReleasedWZ`, {
        orderNumber: details.orderNumber || '',
        wzNumbersSuffix: wzNumbers.length > 0
          ? String(t(`${WARNING_KEY}.deliveredWithoutReleasedWZDocuments`, { wzNumbers: wzNumbers.join(', ') }))
          : '',
      }));
    }
    case 'WZ_MISSING_PURCHASE_INVOICE':
    case 'WZ_BEFORE_PURCHASE_INVOICE':
    case 'WZ_PENDING_STOCK_ISSUE':
      return String(t(`${WARNING_KEY}.wzBeforePurchaseInvoice`));
    case 'PZ_QUANTITY_RECEIVED_EXCEEDS_ORDERED':
      return String(t(`${WARNING_KEY}.pzOverReceipt`, {
        productName: details.productName,
        unit: unit(details.unit),
        quantityReceived: quantity(details.quantityReceived),
        quantityOrdered: quantity(details.quantityOrdered),
        difference: quantity(Math.abs(Number(details.difference) || 0)),
      }));
    case 'PZ_INVOICE_DATE_AFTER_RECEIPT':
      return String(t(`${WARNING_KEY}.pzInvoiceDateAfterReceipt`, {
        invoiceDate: formatDateEuropean(details.invoiceDate),
        receiptDate: formatDateEuropean(details.receiptDate),
      }));
    case 'PZ_INVOICE_NET_AMOUNT_MISMATCH':
      return String(t(`${WARNING_KEY}.pzInvoiceNetAmountMismatch`, {
        productName: details.productName,
        lineValue: money(details.lineValue),
        netAmount: money(details.netAmount),
        currency: details.currency || '',
      }));
    case 'CARGO_OVER_ALLOCATION': {
      const rows = Array.isArray(details.overflow) ? details.overflow : [];
      if (rows.length === 0) return null;
      return rows.map((row: Record<string, unknown>) => {
        // `allowed` from the backend is the cumulative guard ceiling (it
        // includes the tolerance) and must never be shown as free stock, so
        // the free amount is derived here instead.
        const ordered = Number(row.ordered) || 0;
        const allocatedElsewhere = Number(row.allocatedElsewhere) || 0;
        return String(t(`${WARNING_KEY}.cargoOverAllocation`, {
          productName: row.productName,
          attempted: quantity(row.attempted),
          available: quantity(Math.max(ordered - allocatedElsewhere, 0)),
          ordered: quantity(ordered),
          allocatedElsewhere: quantity(allocatedElsewhere),
          tolerance: quantity(CARGO_QUANTITY_TOLERANCE),
        }));
      }).join('\n');
    }
    case 'TRANSPORT_DELIVERY_EXCEEDS_PLANNED':
      return String(t(`${WARNING_KEY}.transportDeliveryExceedsPlanned`, {
        productName: details.productName,
        unit: unit(details.unit),
        deliveredQuantity: quantity(details.deliveredQuantity),
        plannedQuantity: quantity(details.plannedQuantity),
        difference: quantity(Math.abs(Number(details.difference) || 0)),
      }));
    case 'TRANSPORT_ACTUAL_DELIVERY_BEFORE_LOADING':
      return String(t(`${WARNING_KEY}.transportDeliveryBeforeLoading`, {
        actualDelivery: formatDateEuropean(details.actualDelivery),
        loadingDate: formatDateEuropean(details.loadingDate),
      }));
    case 'WZ_CORRECTED_AFTER_INVOICE': {
      const invoiceNumbers = Array.isArray(details.invoiceNumbers) ? details.invoiceNumbers.filter(Boolean) : [];
      return String(t(`${WARNING_KEY}.wzCorrectedAfterInvoice`, {
        wzNumber: details.wzNumber || '',
        invoicesSuffix: invoiceNumbers.length > 0
          ? String(t(`${WARNING_KEY}.wzCorrectedAfterInvoiceNumbers`, { invoiceNumbers: invoiceNumbers.join(', ') }))
          : '',
      }));
    }
    case 'ORDER_CHANGED_AFTER_INVOICE':
      return String(t(`${WARNING_KEY}.orderChangedAfterInvoice`, {
        orderNumber: details.orderNumber || '',
        invoicesSuffix: afterInvoiceNumbers(details, t),
        changes: afterInvoiceChanges(details, t),
      }));
    case 'SUPPLIER_ORDER_CHANGED_AFTER_INVOICE':
      return String(t(`${WARNING_KEY}.supplierOrderChangedAfterInvoice`, {
        orderNumber: details.orderNumber || '',
        invoicesSuffix: afterInvoiceNumbers(details, t),
        changes: afterInvoiceChanges(details, t),
      }));
    case 'SUPPLIER_ORDER_PZ_QUANTITY_REDUCED': {
      const lines = Array.isArray(details.lines) ? details.lines : [];
      if (lines.length === 0) return null;
      return lines.map((line: Record<string, unknown>) => String(t(`${WARNING_KEY}.supplierOrderPZQuantityReduced`, {
        productName: line.name || '',
        unit: unit(line.unit),
        quantityBefore: quantity(line.quantityBefore),
        quantityAfter: quantity(line.quantityAfter),
      }))).join('\n');
    }
    case 'ORDER_ISSUED_OVER_INVOICE': {
      const invoiceNumbers = Array.isArray(details.invoiceNumbers) ? details.invoiceNumbers.filter(Boolean) : [];
      const lines = Array.isArray(details.lines) ? details.lines : [];
      return String(t(`${WARNING_KEY}.orderIssuedOverInvoice`, {
        invoiceNumbers: invoiceNumbers.join(', '),
        quantities: lines.map((line: Record<string, unknown>) => `${quantity(line.quantity)} ${unit(line.unit)}`).join(', '),
      }));
    }
    default:
      return null;
  }
}

/**
 * The sentence plus, for a code this build does not know, the support caption
 * carrying the raw code. A frontend older than the backend lands here, so a
 * new warning code never reaches a screen as a bare code.
 */
export function warningCopy(warning: OperationalWarning, t: TFunction): OperationalWarningCopy {
  const known = sentenceForCode(warning, t);
  if (known !== null) return { text: finishSentence(known), supportCode: null };
  if (isReadableSentence(warning.message)) {
    return { text: warning.message.trim(), supportCode: warning.code || null };
  }
  return { text: String(t(`${WARNING_KEY}.unknown`)), supportCode: warning.code || null };
}

/** Sentence only, for callers that render plain text. */
export function warningText(warning: OperationalWarning, t: TFunction): string {
  return warningCopy(warning, t).text;
}
